'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Eye, EyeOff, Loader2, ChevronRight, ChevronLeft,
  AlertCircle, Building2, UserCircle2, GraduationCap,
  Briefcase, BookOpen, Star, Check,
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface RegisterFormData {
  fullName: string;
  email: string;
  mobile: string;
  password: string;
  confirmPassword: string;
  country: string;
  acceptTerms: boolean;
}

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

type PersonaId = 'enterprise' | 'professional' | 'individual' | 'student' | 'institution' | 'mentor';

interface PersonaCard {
  id: PersonaId;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  description: string;
  color: string;
  borderColor: string;
  bgColor: string;
  badge: string;
}

const personaCards: PersonaCard[] = [
  {
    id: 'enterprise',
    icon: <Building2 size={22} />,
    title: 'Enterprise',
    subtitle: 'I represent an organisation',
    description: 'CXO, CHRO, CDO, or transformation leader driving org-wide AI capability.',
    color: '#C9A84C',
    borderColor: 'rgba(201,168,76,0.35)',
    bgColor: 'rgba(201,168,76,0.06)',
    badge: 'B2B',
  },
  {
    id: 'professional',
    icon: <Briefcase size={22} />,
    title: 'Employee / Professional',
    subtitle: 'I am building my professional capability',
    description: 'Employed professional upskilling in AI, data, and digital transformation.',
    color: '#4DD9FF',
    borderColor: 'rgba(77,217,255,0.3)',
    bgColor: 'rgba(77,217,255,0.05)',
    badge: 'Career',
  },
  {
    id: 'individual',
    icon: <UserCircle2 size={22} />,
    title: 'Individual / Aspirant',
    subtitle: 'I want to develop my skills/career',
    description: 'Self-directed learner, career switcher, or freelancer building AI readiness.',
    color: '#A78BFA',
    borderColor: 'rgba(167,139,250,0.3)',
    bgColor: 'rgba(167,139,250,0.05)',
    badge: 'Self-paced',
  },
  {
    id: 'student',
    icon: <GraduationCap size={22} />,
    title: 'Student',
    subtitle: 'I am currently studying',
    description: 'University or college student building employability and AI-readiness.',
    color: '#34D399',
    borderColor: 'rgba(52,211,153,0.3)',
    bgColor: 'rgba(52,211,153,0.05)',
    badge: 'Academic',
  },
  {
    id: 'institution',
    icon: <BookOpen size={22} />,
    title: 'Institution',
    subtitle: 'I represent a school, college or university',
    description: 'Educational institution measuring student, faculty, and curriculum readiness.',
    color: '#FB923C',
    borderColor: 'rgba(251,146,60,0.3)',
    bgColor: 'rgba(251,146,60,0.05)',
    badge: 'B2B2C',
  },
  {
    id: 'mentor',
    icon: <Star size={22} />,
    title: 'Mentor',
    subtitle: 'I want to mentor organisations or innovators',
    description: 'Expert practitioner contributing coaching, advisory, and domain knowledge.',
    color: '#F472B6',
    borderColor: 'rgba(244,114,182,0.3)',
    bgColor: 'rgba(244,114,182,0.05)',
    badge: 'Expert',
  },
];

const countries = [
  { code: 'IN', name: 'India' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'SG', name: 'Singapore' },
  { code: 'AE', name: 'UAE' },
  { code: 'AU', name: 'Australia' },
  { code: 'CA', name: 'Canada' },
  { code: 'DE', name: 'Germany' },
  { code: 'JP', name: 'Japan' },
  { code: 'ZA', name: 'South Africa' },
];

type RegisterStep = 1 | 2 | 3;

export default function RegisterForm({ onSwitchToLogin }: RegisterFormProps) {
  const [step, setStep] = useState<RegisterStep>(1);
  const [selectedPersona, setSelectedPersona] = useState<PersonaId | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);

  const { signUp } = useAuth();
  const supabase = createClient();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    defaultValues: { country: 'IN' },
  });

  const passwordValue = watch('password');

  const handlePersonaSelect = (personaId: PersonaId) => {
    setSelectedPersona(personaId);
  };

  const handleNextFromPersona = () => {
    if (!selectedPersona) {
      toast.error('Please select your persona to continue');
      return;
    }
    setStep(2);
  };

  const handleNextFromProfile = () => {
    setStep(3);
  };

  const onSubmit = async (data: RegisterFormData) => {
    if (!selectedPersona) return;
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.fullName,
        persona: selectedPersona,
        mobile: data.mobile,
        country: data.country,
      });
      setRegistrationComplete(true);
      toast.success('KCF Intelligence Profile created!', {
        description: `Welcome, ${data.fullName}. Your Digital Twin is being initialised.`,
      });
    } catch (err: any) {
      toast.error('Registration failed', {
        description: err?.message || 'Please try again or use a different email address.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialSignUp = async (provider: 'google' | 'azure') => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: selectedPersona ? { persona: selectedPersona } : undefined,
        },
      });
      if (error) throw error;
    } catch (err: any) {
      toast.error(`${provider} sign-up failed`, { description: err?.message });
    }
  };

  if (registrationComplete) {
    return <RegistrationSuccess selectedPersona={selectedPersona} />;
  }

  return (
    <>
      <Toaster position="bottom-right" theme="dark" richColors />

      <div className="space-y-5">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-800 text-foreground mb-1">
            Create your{' '}
            <span className="text-gradient-gold">KCF Intelligence Profile</span>
          </h2>
          <p className="text-sm text-muted-foreground">
            Your persistent Digital Twin — assessed, tracked, and continuously improved.
          </p>
        </div>

        {/* Step progress */}
        <StepProgress currentStep={step} />

        {/* Step 1: Persona Selection */}
        {step === 1 && (
          <div className="animate-slide-up space-y-4">
            <div>
              <p className="text-sm font-700 text-foreground mb-1">
                How will you use KnowledgeCapital.World?
              </p>
              <p className="text-xs text-muted-foreground">
                Your persona determines your intelligence journey, assessments, and dashboard.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              {personaCards.map((persona) => (
                <PersonaCardItem
                  key={`persona-card-${persona.id}`}
                  persona={persona}
                  isSelected={selectedPersona === persona.id}
                  onSelect={handlePersonaSelect}
                />
              ))}
            </div>

            {selectedPersona && (
              <SelectedPersonaBanner
                persona={personaCards.find((p) => p.id === selectedPersona)!}
              />
            )}

            <button
              onClick={handleNextFromPersona}
              disabled={!selectedPersona}
              className="btn-primary flex items-center justify-center gap-2"
              style={{ minHeight: '44px' }}
            >
              <span>Continue as {selectedPersona ? personaCards.find(p => p.id === selectedPersona)?.title : '...'}</span>
              <ChevronRight size={16} />
            </button>

            <p className="text-center text-xs text-muted-foreground">
              Already have a KCF ID?{' '}
              <button
                onClick={onSwitchToLogin}
                className="text-primary font-600 hover:underline"
              >
                Sign in
              </button>
            </p>
          </div>
        )}

        {/* Step 2: Profile Details */}
        {step === 2 && (
          <form onSubmit={(e) => { e.preventDefault(); handleNextFromProfile(); }} className="animate-slide-up space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronLeft size={14} />
                Change persona
              </button>
              {selectedPersona && (
                <PersonaMiniTag persona={personaCards.find((p) => p.id === selectedPersona)!} />
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-600 text-foreground mb-1.5 tracking-wide">
                  Full Name
                </label>
                <input
                  {...register('fullName', {
                    required: 'Full name is required',
                    minLength: { value: 2, message: 'Name must be at least 2 characters' },
                  })}
                  type="text"
                  placeholder="Your full name"
                  className={`input-field ${errors.fullName ? 'input-error' : ''}`}
                  autoComplete="name"
                />
                {errors.fullName && (
                  <FieldError message={errors.fullName.message!} />
                )}
              </div>

              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5 tracking-wide">
                  Email Address
                </label>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                  })}
                  type="email"
                  placeholder="you@company.com"
                  className={`input-field ${errors.email ? 'input-error' : ''}`}
                  autoComplete="email"
                />
                {errors.email && <FieldError message={errors.email.message!} />}
              </div>

              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5 tracking-wide">
                  Mobile Number
                </label>
                <input
                  {...register('mobile', {
                    required: 'Mobile number is required',
                    pattern: { value: /^[+]?[\d\s-]{8,15}$/, message: 'Enter a valid mobile number' },
                  })}
                  type="tel"
                  placeholder="+91 98765 43210"
                  className={`input-field ${errors.mobile ? 'input-error' : ''}`}
                  autoComplete="tel"
                />
                {errors.mobile && <FieldError message={errors.mobile.message!} />}
              </div>

              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5 tracking-wide">
                  Password
                </label>
                <div className="relative">
                  <input
                    {...register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Minimum 8 characters' },
                      pattern: {
                        value: /^(?=.*[A-Z])(?=.*\d)/,
                        message: 'Must include 1 uppercase letter and 1 number',
                      },
                    })}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create password"
                    className={`input-field pr-9 ${errors.password ? 'input-error' : ''}`}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {errors.password && <FieldError message={errors.password.message!} />}
              </div>

              <div>
                <label className="block text-xs font-600 text-foreground mb-1.5 tracking-wide">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    {...register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (value) => value === passwordValue || 'Passwords do not match',
                    })}
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Repeat password"
                    className={`input-field pr-9 ${errors.confirmPassword ? 'input-error' : ''}`}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {errors.confirmPassword && <FieldError message={errors.confirmPassword.message!} />}
              </div>

              <div className="col-span-2">
                <label className="block text-xs font-600 text-foreground mb-1.5 tracking-wide">
                  Country
                </label>
                <select
                  {...register('country', { required: 'Country is required' })}
                  className="input-field"
                  style={{ appearance: 'none', cursor: 'pointer' }}
                >
                  {countries.map((c) => (
                    <option key={`country-${c.code}`} value={c.code}
                      style={{ background: 'var(--navy-700)', color: 'var(--foreground)' }}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <PasswordStrengthIndicator password={passwordValue || ''} />

            <div className="flex items-start gap-2">
              <input
                {...register('acceptTerms', { required: 'You must accept the terms to continue' })}
                id="accept-terms"
                type="checkbox"
                className="w-4 h-4 rounded accent-primary cursor-pointer mt-0.5 flex-shrink-0"
              />
              <label htmlFor="accept-terms" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
                I agree to KCF&apos;s{' '}
                <a href="#" className="text-primary hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-primary hover:underline">Privacy Policy</a>
                . My intelligence data is encrypted and I control all sharing permissions.
              </label>
            </div>
            {errors.acceptTerms && <FieldError message={errors.acceptTerms.message!} />}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="btn-secondary"
                style={{ width: 'auto', flex: '0 0 auto', padding: '10px 20px' }}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="submit"
                className="btn-primary flex items-center justify-center gap-2 flex-1"
                style={{ minHeight: '44px', width: 'auto' }}
              >
                <span>Review & Create Profile</span>
                <ChevronRight size={16} />
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Review & Confirm */}
        {step === 3 && (
          <form onSubmit={handleSubmit(onSubmit)} className="animate-slide-up space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronLeft size={14} />
                Edit details
              </button>
            </div>

            <ReviewSummary selectedPersona={selectedPersona} personaCards={personaCards} />

            <div
              className="rounded-xl p-4"
              style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)' }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: 'rgba(16,185,129,0.15)' }}
                >
                  <Check size={16} className="text-emerald" />
                </div>
                <div>
                  <p className="text-sm font-700 text-foreground mb-1">
                    Your Digital Twin will be initialised
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    KCF will create a persistent intelligence profile that captures your capability scores,
                    tracks your transformation journey, and continuously updates as you learn, build, and advance.
                  </p>
                </div>
              </div>
            </div>

            <div
              className="rounded-xl p-4"
              style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)' }}
            >
              <p className="text-xs font-700 text-primary mb-2">What happens next</p>
              <div className="space-y-1.5">
                {[
                  'Email verification sent to your registered address',
                  'KCF Intelligence Profile created with your persona configuration',
                  'First assessment recommended based on your persona and goals',
                  'AI Concierge activated — ask KCF anything about your journey',
                ].map((step, idx) => (
                  <div key={`next-step-${idx}`} className="flex items-center gap-2">
                    <span
                      className="w-4 h-4 rounded-full flex items-center justify-center text-2xs font-700 flex-shrink-0"
                      style={{ background: 'rgba(201,168,76,0.15)', color: 'var(--gold-500)' }}
                    >
                      {idx + 1}
                    </span>
                    <p className="text-xs text-muted-foreground">{step}</p>
                  </div>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary flex items-center justify-center gap-2"
              style={{ minHeight: '48px' }}
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Creating Intelligence Profile...</span>
                </>
              ) : (
                <>
                  <span>Create My KCF Intelligence Profile</span>
                  <ChevronRight size={16} />
                </>
              )}
            </button>

            {/* Social sign-up alternatives */}
            <div className="flex items-center gap-3">
              <div className="flex-1 divider-gold" />
              <span className="text-xs text-muted-foreground">or sign up with</span>
              <div className="flex-1 divider-gold" />
            </div>
            <div className="flex gap-2">
              <button type="button" className="btn-social" onClick={() => handleSocialSignUp('google')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                Google
              </button>
              <button type="button" className="btn-social" onClick={() => handleSocialSignUp('azure')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                  <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
                  <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
                  <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
                </svg>
                Microsoft
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}

/* ─────────────────── Sub-components ─────────────────── */

interface StepProgressProps {
  currentStep: RegisterStep;
}

function StepProgress({ currentStep }: StepProgressProps) {
  const steps = [
    { num: 1, label: 'Choose Persona' },
    { num: 2, label: 'Your Details' },
    { num: 3, label: 'Confirm' },
  ];

  return (
    <div className="flex items-center gap-1">
      {steps.map((s, idx) => (
        <React.Fragment key={`step-progress-${s.num}`}>
          <div className="flex items-center gap-1.5">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-2xs font-700 transition-all duration-300"
              style={
                currentStep === s.num
                  ? { background: 'var(--gold-500)', color: 'var(--navy-800)' }
                  : currentStep > s.num
                  ? { background: 'var(--teal-700)', color: '#4DD9FF', border: '1px solid rgba(13,79,108,0.6)' }
                  : { background: 'rgba(26,46,74,0.6)', color: 'var(--muted-foreground)', border: '1px solid var(--navy-500)' }
              }
            >
              {currentStep > s.num ? <Check size={12} /> : s.num}
            </div>
            <span
              className="text-2xs font-600 tracking-wide hidden sm:block transition-all"
              style={
                currentStep === s.num
                  ? { color: 'var(--gold-500)' }
                  : currentStep > s.num
                  ? { color: '#4DD9FF' }
                  : { color: 'var(--muted-foreground)' }
              }
            >
              {s.label}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <div
              className="flex-1 h-px transition-all duration-300"
              style={{
                background: currentStep > s.num
                  ? 'rgba(13,79,108,0.6)'
                  : 'rgba(30,58,90,0.4)',
              }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

interface PersonaCardItemProps {
  persona: PersonaCard;
  isSelected: boolean;
  onSelect: (id: PersonaId) => void;
}

function PersonaCardItem({ persona, isSelected, onSelect }: PersonaCardItemProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(persona.id)}
      className="text-left rounded-xl p-3 transition-all duration-200 persona-card-hover relative overflow-hidden"
      style={{
        background: isSelected ? persona.bgColor : 'rgba(15,30,53,0.7)',
        border: `1px solid ${isSelected ? persona.color : 'rgba(30,58,90,0.6)'}`,
        boxShadow: isSelected ? `0 0 0 1px ${persona.color}30, 0 0 16px ${persona.color}12` : 'none',
      }}
      aria-pressed={isSelected}
      aria-label={`Select ${persona.title} persona`}
    >
      {isSelected && (
        <div
          className="absolute top-2 right-2 w-4 h-4 rounded-full flex items-center justify-center"
          style={{ background: persona.color }}
        >
          <Check size={10} style={{ color: 'var(--navy-800)' }} />
        </div>
      )}
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center mb-2"
        style={{ background: `${persona.color}18`, color: persona.color }}
      >
        {persona.icon}
      </div>
      <p className="text-xs font-700 text-foreground mb-0.5">{persona.title}</p>
      <p className="text-2xs text-muted-foreground leading-tight">{persona.subtitle}</p>
      <span
        className="inline-block mt-1.5 text-2xs font-700 px-1.5 py-0.5 rounded"
        style={{ background: `${persona.color}15`, color: persona.color }}
      >
        {persona.badge}
      </span>
    </button>
  );
}

interface SelectedPersonaBannerProps {
  persona: PersonaCard;
}

function SelectedPersonaBanner({ persona }: SelectedPersonaBannerProps) {
  return (
    <div
      className="rounded-xl p-3 animate-fade-in"
      style={{ background: `${persona.color}08`, border: `1px solid ${persona.color}25` }}
    >
      <div className="flex items-start gap-2.5">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${persona.color}18`, color: persona.color }}
        >
          {persona.icon}
        </div>
        <div>
          <p className="text-xs font-700" style={{ color: persona.color }}>
            {persona.title} Journey Selected
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            {persona.description}
          </p>
        </div>
      </div>
    </div>
  );
}

interface PersonaMiniTagProps {
  persona: PersonaCard;
}

function PersonaMiniTag({ persona }: PersonaMiniTagProps) {
  return (
    <div
      className="flex items-center gap-1 px-2 py-1 rounded-full text-2xs font-700"
      style={{ background: `${persona.color}12`, color: persona.color, border: `1px solid ${persona.color}25` }}
    >
      <span className="w-3 h-3">{persona.icon}</span>
      {persona.title}
    </div>
  );
}

interface FieldErrorProps {
  message: string;
}

function FieldError({ message }: FieldErrorProps) {
  return (
    <div className="flex items-center gap-1.5 mt-1">
      <AlertCircle size={11} className="text-danger flex-shrink-0" />
      <p className="text-xs text-danger">{message}</p>
    </div>
  );
}

interface PasswordStrengthIndicatorProps {
  password: string;
}

function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const getStrength = (pwd: string): { level: number; label: string; color: string } => {
    if (!pwd) return { level: 0, label: '', color: 'var(--navy-500)' };
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    if (score <= 1) return { level: 1, label: 'Weak', color: 'var(--red)' };
    if (score <= 2) return { level: 2, label: 'Fair', color: 'var(--amber)' };
    if (score <= 3) return { level: 3, label: 'Good', color: '#60A5FA' };
    if (score <= 4) return { level: 4, label: 'Strong', color: 'var(--emerald)' };
    return { level: 5, label: 'Very Strong', color: 'var(--gold-500)' };
  };

  const strength = getStrength(password);

  if (!password) return null;

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-1">
        <p className="text-2xs text-muted-foreground">Password strength</p>
        <p className="text-2xs font-600" style={{ color: strength.color }}>{strength.label}</p>
      </div>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((level) => (
          <div
            key={`strength-bar-${level}`}
            className="flex-1 h-1 rounded-full transition-all duration-300"
            style={{
              background: level <= strength.level ? strength.color : 'var(--navy-500)',
            }}
          />
        ))}
      </div>
    </div>
  );
}

interface ReviewSummaryProps {
  selectedPersona: PersonaId | null;
  personaCards: PersonaCard[];
}

function ReviewSummary({ selectedPersona, personaCards }: ReviewSummaryProps) {
  const persona = personaCards.find((p) => p.id === selectedPersona);
  if (!persona) return null;

  const journeyItems = {
    enterprise: ['Enterprise AI Readiness Assessment', 'Organisation Heat Map', 'Team Benchmarking', 'ROI Dashboard', '7-Phase Transformation Roadmap'],
    professional: ['Personal Capability Assessment', 'Career Intelligence Report', 'Skill Gap Analysis', 'Learning Pathway', 'Digital Twin'],
    individual: ['AI Readiness Assessment', 'Career Navigator', 'Personalized Learning Path', 'Portfolio Builder', 'Mentor Access'],
    student: ['Employability Assessment', 'AI Readiness Score', 'Career Pathway Map', 'Project Studio', 'Internship Readiness'],
    institution: ['Institution AI Readiness Assessment', 'Faculty Heat Map', 'Student Capability Analytics', 'Curriculum Gap Analysis', 'Transformation Roadmap'],
    mentor: ['Expert Profile Setup', 'Mentee Matching', 'Session Workspace', 'Impact Dashboard', 'Revenue Tracking'],
  };

  const items = journeyItems[selectedPersona!] || [];

  return (
    <div
      className="rounded-xl p-4"
      style={{ background: `${persona.color}06`, border: `1px solid ${persona.color}20` }}
    >
      <div className="flex items-center gap-2 mb-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: `${persona.color}18`, color: persona.color }}
        >
          {persona.icon}
        </div>
        <div>
          <p className="text-sm font-700 text-foreground">{persona.title} Intelligence Journey</p>
          <p className="text-2xs text-muted-foreground">{persona.description}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-1">
        {items.map((item, idx) => (
          <div key={`journey-item-${idx}`} className="flex items-center gap-2">
            <Check size={11} style={{ color: persona.color, flexShrink: 0 }} />
            <p className="text-xs text-muted-foreground">{item}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────── Registration Success ─────────────────── */

interface RegistrationSuccessProps {
  selectedPersona: PersonaId | null;
}

function RegistrationSuccess({ selectedPersona }: RegistrationSuccessProps) {
  const persona = [
    { id: 'enterprise', label: 'Enterprise Dashboard', color: '#C9A84C' },
    { id: 'professional', label: 'Professional Dashboard', color: '#4DD9FF' },
    { id: 'individual', label: 'Individual Dashboard', color: '#A78BFA' },
    { id: 'student', label: 'Student Dashboard', color: '#34D399' },
    { id: 'institution', label: 'Institution Dashboard', color: '#FB923C' },
    { id: 'mentor', label: 'Mentor Portal', color: '#F472B6' },
  ].find((p) => p.id === selectedPersona);

  return (
    <div className="text-center space-y-5 animate-slide-up py-4">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto"
        style={{ background: 'rgba(16,185,129,0.15)', border: '2px solid rgba(16,185,129,0.4)' }}
      >
        <Check size={28} className="text-emerald" />
      </div>
      <div>
        <h3 className="text-xl font-800 text-foreground mb-2">
          Intelligence Profile Created
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Your KCF Digital Twin has been initialised. Check your email to verify your account and begin your assessment.
        </p>
      </div>

      <div
        className="rounded-xl p-4 text-left"
        style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}
      >
        <p className="text-xs font-700 text-primary mb-2 tracking-wide uppercase">
          Your Journey Starts Here
        </p>
        <p className="text-xs text-muted-foreground mb-3">
          Complete your first assessment to generate your AI Readiness Index and unlock your personalised heat map.
        </p>
        <button
          className="btn-primary flex items-center justify-center gap-2"
          style={{ minHeight: '40px' }}
          onClick={() => { window.location.href = '/dashboard'; }}
        >
          <span>Go to My Dashboard</span>
          <ChevronRight size={14} />
        </button>
      </div>

      {persona && (
        <button
          className="w-full py-2.5 rounded-lg text-sm font-600 transition-all"
          style={{
            background: `${persona.color}10`,
            border: `1px solid ${persona.color}25`,
            color: persona.color,
          }}
          onClick={() => { window.location.href = '/dashboard'; }}
        >
          Go to {persona.label} →
        </button>
      )}
    </div>
  );
}